import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IUser, IPatient } from '@commons/interfaces';
import { INutritionConsultation } from '@commons/interfaces/nutrition-consultation.interface';

@Injectable({
  providedIn: 'root',
})
export class StateService {
  // Sujetos observables existentes
  private currentUserSubject = new BehaviorSubject<IUser | null>(null);
  private currentPatientSubject = new BehaviorSubject<IPatient | null>(null);

  // Nuevo sujeto para consulta nutricional actual
  private currentConsultationSubject =
    new BehaviorSubject<INutritionConsultation | null>(null);

  // Observables públicos
  public currentUser$ = this.currentUserSubject.asObservable();
  public currentPatient$ = this.currentPatientSubject.asObservable();
  public currentConsultation$ = this.currentConsultationSubject.asObservable();

  constructor() {
    // Inicializar desde localStorage si existe
    this.initFromStorage();
  }

  private initFromStorage(): void {
    // Inicializar usuario y paciente (código existente)
    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      this.currentUserSubject.next(JSON.parse(storedUser));
    }

    const storedPatient = localStorage.getItem('currentPatient');
    if (storedPatient) {
      this.currentPatientSubject.next(JSON.parse(storedPatient));
    }

    // Inicializar consulta actual
    const storedConsultation = localStorage.getItem('citaActual');
    if (storedConsultation) {
      this.currentConsultationSubject.next(JSON.parse(storedConsultation));
    }
  }

  // Métodos para el usuario actual
  setCurrentUser(user: IUser | null): void {
    this.currentUserSubject.next(user);
    // Todavía podemos guardar en localStorage para persistencia entre sesiones
    if (user) {
      localStorage.setItem('currentUser', JSON.stringify(user));
    } else {
      localStorage.removeItem('currentUser');
    }
  }

  getCurrentUser(): IUser | null {
    return this.currentUserSubject.value;
  }

  getCurrentPatient(): IPatient | null {
    return this.currentPatientSubject.value;
  }

  // Métodos para consulta nutricional actual
  setCurrentConsultation(consultation: INutritionConsultation): void {
    this.currentConsultationSubject.next(consultation);
    localStorage.setItem('citaActual', JSON.stringify(consultation));
  }

  getCurrentConsultation(): INutritionConsultation | null {
    return this.currentConsultationSubject.value;
  }

  clearConsultation(): void {
    this.currentConsultationSubject.next(null);
    localStorage.removeItem('citaActual');
  }

  // Limpiar el estado (logout)
  clearState(): void {
    this.currentUserSubject.next(null);
    this.currentPatientSubject.next(null);
    this.currentConsultationSubject.next(null);

    localStorage.removeItem('currentUser');
    localStorage.removeItem('currentPatient');
    localStorage.removeItem('citaActual');
  }

  // Métodos para el usuario actual
  setCurrentPatient(patient: IPatient | null): void {
    this.currentPatientSubject.next(patient);

    if (patient) {
      localStorage.setItem('currentPatient', JSON.stringify(patient));
    } else {
      localStorage.removeItem('currentPatient');
    }

    this.clearConsultation();
  }

  /**
   * Recupera el paciente actual guardado en localStorage
   * @returns El paciente guardado o null si no existe
   */
  getSavedPatient(): IPatient | null {
    try {
      const savedPatient = localStorage.getItem('currentPatient');
      if (savedPatient) {
        return JSON.parse(savedPatient);
      }
    } catch (error) {
      console.error('Error recuperando paciente desde localStorage:', error);
    }
    return null;
  }
}
